import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectActiveProducts } from '@paradigm/store/auth';

import {
  useRfqBuilderActions,
  useRfqBuilderView,
} from '@paradigm/features/src/unified-rfqs/ui';

import SelectField from '#/components/UnifiedRfqs/components/SelectField';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { useTrackAction } from '@paradigm/features/src/analytics';
import { FormOption } from '@paradigm/features/src/unified-rfqs/ui/rfq-builder/types';
import { Source } from '@paradigm/features/src/unified-rfqs/entities/products/venue';

export default function VenueSelector() {
  const activeProducts = useSelector(selectActiveProducts);
  const { venue } = useRfqBuilderView();
  const activeVenues = venue.options.filter((v) =>
    activeProducts.includes(v.value),
  );
  const { selectVenue } = useRfqBuilderActions();
  const isMobile = useIsMobile();
  const trackAction = useTrackAction();

  const handleOnChange = useCallback(
    (option: FormOption<Source> | null) => {
      trackAction('update venue', { venueCode: option?.value ?? null });
      if (option != null) selectVenue(option.value);
    },
    [selectVenue, trackAction],
  );

  // The builder always holds a venue, entitled or not: `useDefaultVenue` has
  // none to give a firm entitled to no selectable venue, and entitlements can
  // also drop while the builder is open. Show it only when it is one of the
  // options -- naming a venue the firm cannot trade, over a list that does
  // not offer it, leaves the user unable to pick their way out.
  const selectedVenue =
    activeVenues.find((v) => v.value === venue.selectedOption.value) ?? null;
  // An empty field stays focusable and labelled, unlike a disabled one:
  // react-select gives a disabled input `visibility: hidden`, which drops it
  // and its label out of the accessibility tree.
  const placeholder =
    activeVenues.length > 0 ? undefined : 'No venues available';

  return useMemo(
    () => (
      <SelectField
        aria-label="Select venue"
        options={activeVenues}
        value={selectedVenue}
        onChange={handleOnChange}
        isSearchable={!isMobile}
        placeholder={placeholder}
      />
    ),
    [activeVenues, handleOnChange, isMobile, placeholder, selectedVenue],
  );
}
